import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Calendar, ArrowRight, MapPin } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { ABOUT, BRAND, IMAGES } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BIO = [
  "Jeg er født og opvokset i Sæby og har altid haft en stor interesse for ejendomme, investering og det at skabe gode kundeoplevelser. Det, der driver mig, er kombinationen af struktur, service og muligheden for at hjælpe boligejere med at få mere ud af deres udlejning uden selv at skulle stå med den daglige drift.",
  "Jeg er kendt for at være udadvendt, imødekommende og nysgerrig på mennesker. Jeg sætter stor pris på den personlige relation og tror på, at de bedste samarbejder opstår gennem tillid, god kommunikation og en ærlig dialog.",
  "Med Gudiksen Stays tilbyder vi både møblerede boliger til længere ophold - til virksomheder og deres medarbejdere - og korttidsudlejning til gæster. Samtidig gør vi udlejning lettere for boligejere ved at håndtere den daglige administration, gæstekommunikation og koordinering, så de kan bruge deres tid på det, der betyder mest for dem.",
  "Når jeg ikke arbejder med ejendomme, udlejning eller udviklingen af Gudiksen Stays, elsker jeg at se fodbold, følge med i ejendomsmarkedet og nyde en god øl med venner og familie. Jeg er altid åben for en god snak om ejendomme, Airbnb eller iværksætteri - og kommer du forbi Nordjylland, giver jeg gerne en øl. 🍻",
];

function BusinessCard() {
  return (
    <motion.div
      initial={{ opacity: 0, rotateY: -10, y: 20 }}
      whileInView={{ opacity: 1, rotateY: 0, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
      className="relative w-full max-w-[420px] mx-auto"
      data-testid="business-card"
      style={{ perspective: "1200px" }}
    >
      {/* Folder/envelope flap behind */}
      <div className="absolute -top-3 -left-3 right-3 bottom-3 bg-[#1a1a1a] border border-[#D4AF37]/20 -z-0" />
      <div className="absolute -top-1.5 -left-1.5 right-1.5 bottom-1.5 bg-[#222222] -z-0" />

      {/* Card */}
      <div className="relative bg-[#111111] text-white border border-[#D4AF37]/40 p-7 sm:p-9 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)]">
        <div className="absolute top-0 right-0 w-16 h-16 border-t border-r border-[#D4AF37]/60" />
        <div className="absolute bottom-0 left-0 w-16 h-16 border-b border-l border-[#D4AF37]/60" />

        {/* Logo mark */}
        <div className="flex items-center gap-3 mb-8">
          <img
            src={BRAND.logoUrl}
            alt="Gudiksen Stays"
            className="h-10 w-10 object-contain brightness-0 invert"
          />
          <div className="leading-none">
            <div
              className="font-serif text-base tracking-[0.22em] text-white"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              GUDIKSEN
            </div>
            <div className="text-[9px] tracking-[0.4em] text-[#D4AF37] mt-1">
              - STAYS -
            </div>
          </div>
        </div>

        {/* Name + title */}
        <div className="mb-8">
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#D4AF37] mb-3">
            CEO & Founder
          </div>
          <div
            className="font-serif text-3xl sm:text-[2.25rem] leading-[1.05] text-white font-light"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            Gustav Gudiksen
            <br />
            <span className="italic text-[#D4AF37]">Nielsen</span>
          </div>
        </div>

        {/* Contact lines */}
        <div className="space-y-3 pt-6 border-t border-white/10">
          <a
            href={`mailto:${BRAND.email}`}
            className="flex items-center gap-3 text-sm text-white/85 hover:text-[#D4AF37] transition-colors"
            data-testid="card-email"
          >
            <Mail size={14} className="text-[#D4AF37] shrink-0" strokeWidth={1.5} />
            <span className="break-all">{BRAND.email}</span>
          </a>
          <a
            href={`tel:${BRAND.phoneRaw}`}
            className="flex items-center gap-3 text-sm text-white/85 hover:text-[#D4AF37] transition-colors"
            data-testid="card-phone"
          >
            <Phone size={14} className="text-[#D4AF37] shrink-0" strokeWidth={1.5} />
            <span>{BRAND.phone}</span>
          </a>
          <div
            className="flex items-center gap-3 text-sm text-white/85"
            data-testid="card-address"
          >
            <MapPin size={14} className="text-[#D4AF37] shrink-0" strokeWidth={1.5} />
            <span>
              {BRAND.address.street}, {BRAND.address.city}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function BookingForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    property_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Udfyld venligst navn, e-mail og besked.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        message: `[Booking-anmodning fra Om os-side]\n\n${form.message}`,
      });
      toast.success("Tak - Gustav vender tilbage hurtigst muligt.");
      setForm({ name: "", email: "", phone: "", property_type: "", message: "" });
    } catch (err) {
      toast.error("Kunne ikke sende. Prøv igen om et øjeblik.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="book-form"
      data-testid="omos-booking-section"
      className="bg-[#F8F8F8] py-16 sm:py-24"
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <p className="overline mb-5 text-[10px] sm:text-xs">
              <span className="inline-block w-8 sm:w-10 h-px bg-[#D4AF37] align-middle mr-3 sm:mr-4" />
              Lad os mødes
            </p>
            <h2
              className="font-serif text-[2.25rem] sm:text-5xl lg:text-[3.5rem] text-[#111111] font-light tracking-tight leading-[1.05]"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Book et møde med{" "}
              <span className="italic text-[#C9A227]">Gustav</span>.
            </h2>
            <p className="mt-6 text-[#555555] font-light leading-relaxed max-w-md">
              Skriv en kort besked om din bolig - eller ring direkte. Vi finder
              et tidspunkt der passer dig.
            </p>

            <div className="mt-10 space-y-6">
              <a
                href={`tel:${BRAND.phoneRaw}`}
                data-testid="omos-call-link"
                className="flex items-center gap-4 group"
              >
                <span className="w-12 h-12 rounded-full border border-[#111111]/15 group-hover:border-[#D4AF37] flex items-center justify-center transition-colors">
                  <Phone size={18} strokeWidth={1.5} className="text-[#111111] group-hover:text-[#D4AF37]" />
                </span>
                <span>
                  <span className="block overline" style={{ color: "#C9A227" }}>
                    Ring direkte
                  </span>
                  <span
                    className="font-serif text-2xl text-[#111111] link-underline"
                    style={{ fontFamily: "Fraunces, serif" }}
                  >
                    {BRAND.phone}
                  </span>
                </span>
              </a>

              <a
                href={BRAND.calendlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="omos-calendly-link"
                className="flex items-center gap-4 group"
              >
                <span className="w-12 h-12 rounded-full border border-[#111111]/15 group-hover:border-[#D4AF37] flex items-center justify-center transition-colors">
                  <Calendar size={18} strokeWidth={1.5} className="text-[#111111] group-hover:text-[#D4AF37]" />
                </span>
                <span>
                  <span className="block overline" style={{ color: "#C9A227" }}>
                    Book i kalenderen
                  </span>
                  <span
                    className="font-serif text-2xl text-[#111111] link-underline"
                    style={{ fontFamily: "Fraunces, serif" }}
                  >
                    30 min uforpligtende samtale
                  </span>
                </span>
              </a>
            </div>
          </motion.div>

          <motion.form
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-7 lg:pl-12 lg:border-l lg:border-black/10"
            data-testid="omos-booking-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <label className="overline block mb-3">Navn *</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={update("name")}
                  className="field-line"
                  placeholder="Dit fulde navn"
                  data-testid="omos-name-input"
                />
              </div>
              <div>
                <label className="overline block mb-3">E-mail *</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  className="field-line"
                  placeholder="dig@email.dk"
                  data-testid="omos-email-input"
                />
              </div>
              <div>
                <label className="overline block mb-3">Telefon</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  className="field-line"
                  placeholder="+45 ..."
                  data-testid="omos-phone-input"
                />
              </div>
              <div>
                <label className="overline block mb-3">Type bolig</label>
                <input
                  type="text"
                  value={form.property_type}
                  onChange={update("property_type")}
                  className="field-line"
                  placeholder="Lejlighed, sommerhus, villa ..."
                  data-testid="omos-property-input"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="overline block mb-3">Besked *</label>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  className="field-line resize-none"
                  placeholder="Fortæl kort om din bolig og det du gerne vil vide ..."
                  data-testid="omos-message-input"
                />
              </div>
            </div>

            <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <button
                type="submit"
                disabled={submitting}
                data-testid="omos-submit-btn"
                className="inline-flex items-center gap-3 bg-[#111111] text-white px-7 py-4 text-xs uppercase tracking-[0.18em] hover:bg-[#D4AF37] hover:text-[#111111] transition-colors duration-500 disabled:opacity-60"
              >
                <span>{submitting ? "Sender ..." : "Send booking-anmodning"}</span>
                <ArrowRight size={16} />
              </button>
              <p className="text-xs text-[#555555] tracking-wide max-w-sm">
                Vi vender tilbage inden for 24 timer - typisk samme dag.
              </p>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

export default function OmOs() {
  return (
    <div className="bg-white text-[#222222]" data-testid="om-os-page">
      <Header />

      {/* Hero / Intro */}
      <section className="relative bg-[#111111] text-white pt-28 sm:pt-36 pb-20 sm:pb-28 overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[36rem] h-[36rem] glow-gold pointer-events-none" />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.04] select-none">
          <span
            className="font-serif text-[28vw] leading-none italic"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            Gustav
          </span>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="overline mb-5 text-[10px] sm:text-xs" style={{ color: "#D4AF37" }}>
            <span className="inline-block w-8 sm:w-10 h-px bg-[#D4AF37] align-middle mr-3 sm:mr-4" />
            Mød manden bag
          </p>
          <h1
            className="font-serif text-[2.5rem] sm:text-6xl lg:text-[6rem] text-white font-light tracking-tight leading-[1.02] max-w-4xl"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            Mød{" "}
            <span className="italic text-[#D4AF37]">Gustav</span>.
          </h1>
          <p className="mt-6 text-white/70 text-lg sm:text-xl font-light leading-relaxed max-w-2xl">
            Jeg hedder Gustav Gudiksen Nielsen og står bag Gudiksen Stays -
            møblerede boliger til korte og længere ophold, og professionel
            drift for boligejere.
          </p>
        </div>
      </section>

      {/* What we do + our goal */}
      <section className="bg-[#F1EFEA] py-16 sm:py-24" data-testid="omos-what-we-do">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-3xl mb-12 sm:mb-16">
            <p className="overline mb-5 text-[10px] sm:text-xs">
              <span className="inline-block w-8 sm:w-10 h-px bg-[#D4AF37] align-middle mr-3 sm:mr-4" />
              Hvad vi laver
            </p>
            <h2
              className="font-serif text-[2.25rem] sm:text-5xl lg:text-[3.5rem] text-[#111111] font-light tracking-tight leading-[1.05]"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Møblerede boliger til <em className="italic text-[#C9A227]">længere ophold</em> -
              og <em className="italic text-[#C9A227]">korttidsudlejning</em>.
            </h2>
            <p className="mt-6 text-[#444444] text-base sm:text-lg font-light leading-relaxed">
              {ABOUT.intro}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-black/10 border border-black/10">
            {ABOUT.offerings.map((o, i) => (
              <motion.div
                key={o.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="bg-white p-7 sm:p-8"
                data-testid={`omos-offering-${o.n}`}
              >
                <div className="num-tag text-4xl sm:text-5xl" style={{ fontFamily: "Fraunces, serif" }}>
                  {o.n}
                </div>
                <div className="h-px w-10 bg-[#D4AF37] my-5" />
                <h3
                  className="font-serif text-xl sm:text-2xl text-[#111111] font-light leading-tight"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  {o.title}
                </h3>
                <p className="mt-3 text-sm text-[#555555] font-light leading-relaxed">{o.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="mt-12 sm:mt-16 bg-[#111111] text-white p-8 sm:p-12 lg:p-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start"
            data-testid="omos-goal"
          >
            <div className="lg:col-span-4">
              <p className="overline mb-4 text-[10px] sm:text-xs" style={{ color: "#D4AF37" }}>
                <span className="inline-block w-8 h-px bg-[#D4AF37] align-middle mr-3" />
                Vores mål
              </p>
              <h3
                className="font-serif text-[1.75rem] sm:text-4xl font-light leading-tight tracking-tight"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                Danmarks foretrukne partner for{" "}
                <em className="italic text-[#D4AF37]">møblerede ophold</em>.
              </h3>
            </div>
            <p className="lg:col-span-8 text-white/75 text-base sm:text-lg font-light leading-relaxed">
              {ABOUT.goal}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Portrait + bio - editorial split layout */}
      <section className="bg-white py-16 sm:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            {/* Left: large editorial portrait */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-120px" }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-5 relative lg:sticky lg:top-28 self-start"
            >
              <div className="relative max-w-[340px] sm:max-w-[400px] mx-auto lg:mx-0">
                {/* Soft gold glow */}
                <div className="absolute inset-0 rounded-full bg-[#D4AF37]/15 blur-3xl scale-[1.15] pointer-events-none" />
                {/* Outer gold ring */}
                <div className="absolute inset-0 rounded-full border border-[#D4AF37]/30 scale-[1.05] pointer-events-none" />

                {/* Editorial label TOP */}
                <div className="absolute -top-2 right-0 sm:right-4 z-10 bg-[#D4AF37] text-[#111111] px-4 py-2 shadow-lg">
                  <div className="text-[9px] tracking-[0.32em] uppercase opacity-70">
                    Sæby · Danmark
                  </div>
                  <div
                    className="font-serif text-base italic leading-none mt-1"
                    style={{ fontFamily: "Fraunces, serif" }}
                  >
                    Est. 2026
                  </div>
                </div>

                {/* Circular portrait - head clearly visible */}
                <div className="relative aspect-square rounded-full overflow-hidden bg-gradient-to-b from-[#1f1f1f] via-[#141414] to-[#0a0a0a] border border-[#D4AF37]/40 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.6)] flex items-end justify-center">
                  <img
                    src={IMAGES.portrait}
                    alt="Gustav Gudiksen Nielsen - CEO & Founder, Gudiksen Stays"
                    className="h-[92%] w-auto object-contain object-bottom"
                  />
                </div>

                {/* Editorial caption BOTTOM */}
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 z-10 bg-white text-[#111111] px-5 py-3 shadow-xl border border-black/5 whitespace-nowrap">
                  <div className="text-[9px] tracking-[0.3em] uppercase text-[#C9A227] text-center">
                    CEO & Founder
                  </div>
                  <div
                    className="font-serif text-base sm:text-lg leading-none mt-1 font-normal text-center"
                    style={{ fontFamily: "Fraunces, serif" }}
                  >
                    Gustav Gudiksen <em className="italic">Nielsen</em>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right: bio */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.9, delay: 0.15, ease: "easeOut" }}
              className="lg:col-span-7"
            >
              <p className="overline mb-5 text-[10px] sm:text-xs">
                <span className="inline-block w-8 sm:w-10 h-px bg-[#D4AF37] align-middle mr-3 sm:mr-4" />
                Personligt
              </p>
              <h2
                className="font-serif text-[2.25rem] sm:text-5xl lg:text-[3.75rem] text-[#111111] font-light tracking-tight leading-[1.05]"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                Ejendomme, mennesker og{" "}
                <em className="italic text-[#C9A227] font-light">en god øl</em>.
              </h2>

              <div className="mt-10 space-y-6 text-[#222222] text-base sm:text-lg font-light leading-relaxed">
                {BIO.map((p, i) => (
                  <p
                    key={i}
                    className={i === 1 ? "text-[#444444]" : ""}
                    data-testid={`bio-paragraph-${i}`}
                  >
                    {p}
                  </p>
                ))}
              </div>

              <div className="mt-12 grid grid-cols-3 gap-6 sm:gap-8 border-t border-black/10 pt-10">
                {[
                  { k: "Sæby", v: "Født og opvokset" },
                  { k: "CEO", v: "& Founder" },
                  { k: "Premium", v: "Boutique co-host" },
                ].map((s) => (
                  <div key={s.k}>
                    <div
                      className="font-serif text-2xl sm:text-3xl text-[#111111] font-light leading-none"
                      style={{ fontFamily: "Fraunces, serif" }}
                    >
                      {s.k}
                    </div>
                    <div className="text-[10px] tracking-[0.18em] uppercase text-[#555555] mt-2 leading-tight">
                      {s.v}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Dedicated business card section - full row */}
      <section className="bg-[#F8F8F8] py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <p className="overline mb-5 text-[10px] sm:text-xs">
                <span className="inline-block w-8 sm:w-10 h-px bg-[#D4AF37] align-middle mr-3 sm:mr-4" />
                Mit visitkort
              </p>
              <h3
                className="font-serif text-[2rem] sm:text-4xl lg:text-5xl text-[#111111] font-light tracking-tight leading-[1.05]"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                Tag fat i mig{" "}
                <em className="italic text-[#C9A227]">direkte</em>.
              </h3>
              <p className="mt-6 text-[#555555] font-light leading-relaxed max-w-md">
                Jeg svarer personligt på enhver henvendelse. Ring, skriv eller
                book et møde - det første step koster ingenting.
              </p>
            </div>
            <div className="lg:col-span-7 flex justify-center lg:justify-end">
              <BusinessCard />
            </div>
          </div>
        </div>
      </section>

      <BookingForm />

      <Footer />
    </div>
  );
}
